/**
 * OpenOrdersTable — Channel-level breakdown of open sales orders.
 * Mirrors the Performance Summary column order:
 *   Channel | Actuals | Budget To Go | Open Orders | Prior Year Full | Actuals vs Prorated Budget | Actuals vs PY To Date
 */

import { CalcTooltip } from "./CalcTooltip";
import { ChannelData } from "../utils/PowerBIApiCall";
import { formatCurrency } from "../utils/format";
import { SERIES } from "../utils/chartTheme";

interface OpenOrdersTableProps {
  totalOpenOrders: number;
  channels: ChannelData[];
  suppressBudget?: boolean;
}

export function OpenOrdersTable({
  totalOpenOrders,
  channels,
  suppressBudget,
}: OpenOrdersTableProps) {
  if (totalOpenOrders <= 0 || channels.length === 0) return null;

  // Detect if any channel has PY data — hide PY columns entirely if none do
  const hasPYData = channels.some(
    (ch) => ch.pySalesFullMonth > 0 || ch.pyMtdSales > 0,
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <svg
            className="w-5 h-5 text-emerald-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">
            Open Sales Orders
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Total</span>
          <span className="text-lg font-bold text-emerald-600 tabular-nums">
            {formatCurrency(totalOpenOrders)}
          </span>
        </div>
      </div>

      {/* Channel Breakdown */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-wmp-50/70 border-b border-gray-200">
              <th className="text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                Channel
              </th>
              <th className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                Actuals
              </th>
              <th className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                Budget To Go
              </th>
              <th className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                Open Orders
              </th>
              <th className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                Monthly Budget
              </th>
              {hasPYData && (
                <th className="text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5">
                  Prior Year Full Month Actual
                </th>
              )}
              <th className="text-center text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 min-w-[140px]">
                Actuals vs Prorated Budget
              </th>
              {hasPYData && (
                <th className="text-center text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 min-w-[120px]">
                  Actuals vs PY To Date
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {channels.map((ch) => {
              const hasPYFull = ch.pySalesFullMonth > 0;
              const hasPYMtd = ch.pyMtdSales > 0;
              // vs PY To Date uses MTD PY (prorated), matching Performance Summary
              const vsPYPct = hasPYMtd
                ? ((ch.mtdSalesTotal - ch.pyMtdSales) / ch.pyMtdSales) * 100
                : 0;
              const vsPYRatio = hasPYMtd
                ? (ch.mtdSalesTotal / ch.pyMtdSales) * 100
                : 0;
              const vsPYColor =
                vsPYRatio >= 100
                  ? SERIES.positive
                  : vsPYRatio >= 95
                    ? SERIES.warning
                    : SERIES.negative;
              const vsPYBarWidth = Math.min(Math.max(vsPYRatio, 0), 100);

              const budgetToGo = ch.mtdSalesTotal - ch.monthlyBudget;
              const budgetToGoColor =
                !suppressBudget && budgetToGo >= 0
                  ? SERIES.positive
                  : SERIES.negative;

              // Use prorated budget for pacing (mirrors Performance Summary: Actual ÷ Budget Prorated)
              const pacingBudget =
                ch.proratedBudget > 0 ? ch.proratedBudget : ch.monthlyBudget;
              const pacingPct =
                !suppressBudget && pacingBudget > 0
                  ? (ch.mtdSalesTotal / pacingBudget) * 100
                  : 0;
              const pacingColor =
                pacingPct >= 100
                  ? SERIES.positive
                  : pacingPct >= 95
                    ? SERIES.warning
                    : SERIES.negative;
              const barWidth = Math.min(Math.max(pacingPct, 0), 100);

              return (
                <tr
                  key={ch.name}
                  className="border-b border-gray-50 last:border-b-0 hover:bg-wmp-50/50 transition-colors"
                >
                  {/* Channel */}
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {ch.name}
                  </td>

                  {/* Actuals */}
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700 text-right tabular-nums">
                    {formatCurrency(ch.mtdSalesTotal)}
                  </td>

                  {/* Budget To Go */}
                  <td
                    className="px-4 py-3 text-sm font-semibold text-right tabular-nums"
                    style={{
                      color: suppressBudget ? undefined : budgetToGoColor,
                    }}
                  >
                    {suppressBudget ? (
                      <span className="text-gray-400">N/A</span>
                    ) : (
                      <CalcTooltip
                        formula="Actuals − Monthly Budget"
                        values={`= ${formatCurrency(ch.mtdSalesTotal)} − ${formatCurrency(ch.monthlyBudget)} = ${formatCurrency(budgetToGo)}`}
                      >
                        <span>{formatCurrency(budgetToGo)}</span>
                      </CalcTooltip>
                    )}
                  </td>

                  {/* Open Orders */}
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-600 text-right tabular-nums">
                    {formatCurrency(ch.amount)}
                  </td>

                  {/* Monthly Budget */}
                  <td className="px-4 py-3 text-sm text-gray-700 text-right tabular-nums font-medium">
                    {suppressBudget ? (
                      <span className="text-gray-400">N/A</span>
                    ) : (
                      formatCurrency(ch.monthlyBudget)
                    )}
                  </td>

                  {/* Prior Year Full */}
                  {hasPYData && (
                    <td className="px-4 py-3 text-sm text-gray-500 text-right tabular-nums">
                      {hasPYFull ? formatCurrency(ch.pySalesFullMonth) : "—"}
                    </td>
                  )}

                  {/* Actuals vs Prorated Budget */}
                  <td className="px-4 py-3 text-center">
                    {suppressBudget ? (
                      <span className="text-sm text-gray-400">N/A</span>
                    ) : (
                      <CalcTooltip
                        formula="Actuals ÷ Budget (Prorated)"
                        values={`= ${formatCurrency(ch.mtdSalesTotal)} ÷ ${formatCurrency(pacingBudget)} = ${pacingPct.toFixed(1)}%`}
                      >
                        <div>
                          <span
                            className="text-base font-bold tabular-nums"
                            style={{ color: pacingColor }}
                          >
                            {pacingPct.toFixed(1)}%
                          </span>
                          <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
                            <div
                              className="h-1.5 rounded-full transition-all"
                              style={{
                                width: `${barWidth}%`,
                                backgroundColor: pacingColor,
                              }}
                            />
                          </div>
                          <span className="text-2xs text-gray-400 tabular-nums">
                            {formatCurrency(pacingBudget)}
                          </span>
                        </div>
                      </CalcTooltip>
                    )}
                  </td>

                  {/* Actuals vs PY To Date */}
                  {hasPYData && (
                    <td className="px-4 py-3 text-center">
                      {hasPYMtd ? (
                        <CalcTooltip
                          formula="(Actuals − PY MTD) ÷ PY MTD"
                          values={`= (${formatCurrency(ch.mtdSalesTotal)} − ${formatCurrency(ch.pyMtdSales)}) ÷ ${formatCurrency(ch.pyMtdSales)} = ${vsPYPct >= 0 ? "+" : ""}${vsPYPct.toFixed(1)}%`}
                        >
                          <div>
                            <span
                              className="text-base font-bold tabular-nums"
                              style={{ color: vsPYColor }}
                            >
                              {vsPYPct >= 0 ? "+" : ""}
                              {vsPYPct.toFixed(1)}%
                            </span>
                            <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1">
                              <div
                                className="h-1.5 rounded-full transition-all"
                                style={{
                                  width: `${vsPYBarWidth}%`,
                                  backgroundColor: vsPYColor,
                                }}
                              />
                            </div>
                            <span className="text-2xs text-gray-400 tabular-nums">
                              PY: {formatCurrency(ch.pyMtdSales)}
                            </span>
                          </div>
                        </CalcTooltip>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
